import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { LogoComponent } from 'app/shared/components/logo.component';
import { LinkButtonComponent } from 'app/shared/components/link-button.component';
import { FeedbackTriggerButtonComponent } from 'app/shared/components/feedback-trigger-button.component';
import { UserComponent } from '../../common/user/user.component';
import { ImpersonationBannerComponent } from '../../common/impersonation-banner/impersonation-banner.component';
import { OnboardingShellService } from '../../onboarding-shell.service';

/**
 * Onboarding shell (Figma "Carrier onboarding"). A deliberately chrome-less layout: no navigation,
 * no search, no widgets — only a top bar with the logo, the onboarding label and the progress
 * action, on the onboarding surface background.
 *
 * Selected by {@link LayoutComponent} for pages whose template is `onboarding-status`, so both the
 * welcome screen and the wizard share it. Label and save action come from
 * {@link OnboardingShellService}, registered by the hosted page.
 */
@Component({
  selector: 'onboarding-layout',
  imports: [
    RouterOutlet,
    MatProgressSpinnerModule,
    FuseLoadingBarComponent,
    LogoComponent,
    LinkButtonComponent,
    UserComponent,
    ImpersonationBannerComponent,
    FeedbackTriggerButtonComponent,
  ],
  template: `
    <fuse-loading-bar></fuse-loading-bar>

    <div class="dd-onboarding-shell flex min-h-screen w-full flex-col">
      <app-impersonation-banner></app-impersonation-banner>

      <header
        class="relative z-49 flex h-16 w-full flex-none items-center gap-4 border-b border-gray-200 bg-card px-4 md:px-6 dark:border-gray-700"
        data-cy="onboarding-topbar">
        <div class="flex min-w-0 items-center gap-3">
          <!-- Onboarding users have no dashboard access: AuthGuard bounces them from any
               non-onboarding route straight back to /onboarding (auth.guard.ts:37), so a
               dashboard link here would just redirect back to this same page. -->
          <app-logo [linkToDashboard]="false"></app-logo>
          @if (shell.title(); as title) {
            <span class="h-6 w-px flex-none bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
            <span class="truncate text-[15px] leading-none text-accent" data-cy="onboarding-topbar-title">
              {{ title }}
            </span>
          }
        </div>

        <div class="ml-auto flex items-center gap-4">
          @if (shell.canSaveProgress()) {
            <button
              dd-link-button
              type="button"
              icon="heroicons_outline:arrow-down-tray"
              [disabled]="shell.saving()"
              (click)="shell.saveProgress()"
              data-cy="onboarding-save-progress">
              @if (shell.saving()) {
                <mat-spinner diameter="16"></mat-spinner>
              }
              Voortgang opslaan
            </button>
          }
          <app-feedback-trigger-button></app-feedback-trigger-button>
          <user></user>
        </div>
      </header>

      <div class="flex flex-auto flex-col">
        <router-outlet></router-outlet>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: `
    /*
      Onboarding surface (Figma: "Backgrounds for onboarding have a custom surface colour, and
      image with opacity"). The tint is built from gradients instead of a bitmap so it scales to
      any viewport and keeps working in dark mode. Both schemes are defined explicitly — the app
      applies 'light'/'dark' as a class on <html>/<body>, so a single hardcoded value would break
      the other scheme.
    */
    .dd-onboarding-shell {
      --dd-onboarding-bg: #f7f8ff;
      --dd-onboarding-tint-a: rgb(99 102 241 / 10%);
      --dd-onboarding-tint-b: rgb(139 92 246 / 7%);

      position: relative;
      isolation: isolate;
      background-color: var(--dd-onboarding-bg);
    }

    /*
      The tint lives on its own viewport-fixed layer rather than on the shell as
      'background-attachment: fixed'. A fixed background cannot be composited, so the browser
      repainted the whole three-gradient stack on every scroll frame of a long onboarding form.
      Negative z-index keeps it above the shell's background colour and below all content, so no
      child needs a stacking rule of its own; 'isolation' above stops it escaping the shell.
    */
    .dd-onboarding-shell::before {
      content: '';
      position: fixed;
      inset: 0;
      z-index: -1;
      pointer-events: none;
      background-image:
        radial-gradient(60rem 40rem at 8% 0%, var(--dd-onboarding-tint-a), transparent 60%),
        radial-gradient(48rem 36rem at 92% 8%, var(--dd-onboarding-tint-b), transparent 55%),
        radial-gradient(52rem 44rem at 50% 100%, var(--dd-onboarding-tint-a), transparent 65%);
    }

    .dark .dd-onboarding-shell {
      --dd-onboarding-bg: #121212;
      --dd-onboarding-tint-a: rgb(129 140 248 / 12%);
      --dd-onboarding-tint-b: rgb(167 139 250 / 9%);
    }

    /* The top bar spinner sits inline in the link button, left of the label. */
    .dd-onboarding-shell [data-cy='onboarding-save-progress'] mat-spinner {
      width: 16px;
      height: 16px;
    }
  `,
})
export class OnboardingLayoutComponent {
  readonly shell = inject(OnboardingShellService);
}
